import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { NgbDropdown, NgbDropdownModule, NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import { HubButtonComponent } from '@shared/atoms/hub-button/hub-button.component';
import {
  OperationToast,
  OperationToastType,
} from '@shared/atoms/hub-operation-toasts/operation-toast.domain';
import { OperationToastsService } from '@shared/atoms/hub-operation-toasts/operation-toasts.service';

@Component({
  selector: 'hub-operation-toasts',
  templateUrl: './operation-toasts.html',
  styleUrl: './operation-toasts.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NgbToastModule, NgbDropdownModule, HubButtonComponent],
})
export class OperationToastsComponent {
  private readonly operationsDropdown = viewChild.required<NgbDropdown>('operationsDropdown');

  private readonly operationToastsService = inject(OperationToastsService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly toasts = this.operationToastsService.toasts;
  readonly OperationToastType = OperationToastType;
  readonly toastCount = computed(() => this.toasts().length);
  readonly hasExpandedToast = computed(() => this.toasts().some((toast) => toast.isExpanded));

  private previousToastCount = 0;

  constructor() {
    effect(() => {
      const currentCount = this.toasts().length;
      const previousCount = this.previousToastCount;

      if (currentCount > previousCount && currentCount > 0) {
        this.previousToastCount = currentCount;
        untracked(() => {
          requestAnimationFrame(() => {
            const dropdown = this.operationsDropdown();
            if (dropdown && !dropdown.isOpen()) {
              dropdown.toggle();
              this.cdr.detectChanges();
            }
          });
        });
      } else if (currentCount === 0) {
        this.previousToastCount = 0;
      }
    });
  }

  isTemplate(toast: OperationToast): boolean {
    return toast.textOrTpl instanceof TemplateRef;
  }

  onHidden(toast: OperationToast): void {
    this.operationToastsService.remove(toast);
  }

  onClose(toast: OperationToast): void {
    this.operationToastsService.remove(toast);
  }

  onExpand(toast: OperationToast): void {
    this.operationToastsService.toggleExpand(toast);
  }
}
